import { useCallback } from 'react';
import { PrintRow } from '.';
import { useBridgeApi } from '~Hooks';

/**
 * Epson ePOS Direct Print
 *
 * Sends print jobs directly to Epson printers via their built-in HTTP ePOS endpoint,
 * bypassing the native app bridge entirely. Includes automatic retry with ping checks.
 *
 * Uses the same XML protocol as the epson-printer-sdk package.
 * Endpoint: http://{printerIp}/cgi-bin/epos/service.cgi?devid=local_printer&timeout=10000
 */

const EPSON_REQUEST_TIMEOUT_MS = 10000;

const SOAP_HEADER =
  '<?xml version="1.0" encoding="utf-8"?><s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"><s:Header><parameter xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print" /></s:Header><s:Body>';
const SOAP_FOOTER = '</s:Body></s:Envelope>';

/**
 * Escapes XML markup characters — matches the epson-printer-sdk escapeMarkup exactly
 */
const escapeMarkup = (s: string): string => {
  const markup = /[<>&'"\t\n\r]/g;
  if (markup.test(s)) {
    s = s.replace(markup, (c) => {
      switch (c) {
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '&':
          return '&amp;';
        case "'":
          return '&apos;';
        case '"':
          return '&quot;';
        case '\t':
          return '&#9;';
        case '\n':
          return '&#10;';
        case '\r':
          return '&#13;';
        default:
          return c;
      }
    });
  }
  return s;
};

type EpsonAlignment = 'left' | 'center' | 'right';

const mapAlignment = (alignment?: string): EpsonAlignment => {
  switch (alignment?.toUpperCase()) {
    case 'CENTER':
      return 'center';
    case 'RIGHT':
      return 'right';
    default:
      return 'left';
  }
};

/**
 * Maps font size number to ePOS width/height scale factors (1-8)
 */
const mapFontSize = (fontSize?: number): { width: number; height: number } => {
  if (!fontSize || fontSize <= 24) return { width: 1, height: 1 };
  if (fontSize <= 35) return { width: 2, height: 2 };
  if (fontSize <= 48) return { width: 3, height: 3 };
  if (fontSize <= 64) return { width: 4, height: 4 };
  return { width: 5, height: 5 };
};

/**
 * Converts a base64 PNG/JPEG image to monochrome raster data for the ePOS <image> element.
 * Uses an offscreen canvas to decode the image, convert to grayscale, then pack into
 * 1-bit-per-pixel raster data (MSB first) as required by the ePOS protocol.
 *
 * The image is scaled to fit the printer's paper width (typically 576 dots for 80mm paper).
 * Width is rounded up to a multiple of 8 as required by the protocol.
 */
const PRINTER_WIDTH_DOTS = 576; // 80mm paper at 203 DPI

const convertImageToEposRaster = async (
  base64Image: string,
): Promise<{ rasterBase64: string; width: number; height: number }> => {
  // Strip data URL prefix if present
  const cleanBase64 = base64Image.replace(/^data:image\/[a-z]+;base64,/, '');

  // Load image into an HTMLImageElement
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image for Epson raster conversion'));
    image.src = `data:image/png;base64,${cleanBase64}`;
  });

  // Scale to printer width, maintaining aspect ratio
  const scale = Math.min(1, PRINTER_WIDTH_DOTS / img.width);
  // Width must be a multiple of 8 for ePOS raster format
  const width = Math.ceil(img.width * scale / 8) * 8;
  const height = Math.round(img.height * scale);

  // Draw image onto canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  // White background (thermal printers treat 0 as white)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  const imageData = ctx.getImageData(0, 0, width, height);
  const pixels = imageData.data;

  // Pack into 1-bit-per-pixel monochrome (MSB first, 1 = black, 0 = white)
  const bytesPerRow = width / 8;
  const rasterBytes = new Uint8Array(bytesPerRow * height);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const pixelOffset = (y * width + x) * 4;
      const r = pixels[pixelOffset];
      const g = pixels[pixelOffset + 1];
      const b = pixels[pixelOffset + 2];
      const a = pixels[pixelOffset + 3];

      // Convert to grayscale with luminance formula, threshold at 128
      // Transparent pixels are treated as white
      const grayscale = a < 128 ? 255 : Math.round(0.299 * r + 0.587 * g + 0.114 * b);
      const isBlack = grayscale < 128;

      if (isBlack) {
        const byteIndex = y * bytesPerRow + Math.floor(x / 8);
        const bitIndex = 7 - (x % 8);
        rasterBytes[byteIndex] |= (1 << bitIndex);
      }
    }
  }

  // Convert to base64
  let binary = '';
  for (let i = 0; i < rasterBytes.length; i++) {
    binary += String.fromCharCode(rasterBytes[i]);
  }
  const rasterBase64 = btoa(binary);

  return { rasterBase64, width, height };
};

/**
 * Builds ePOS XML message from PrintRow[] data.
 *
 * Follows the epson-printer-sdk pattern where formatting and content are separate elements:
 *   <text align="center"/>           ← sets alignment state
 *   <text width="2" height="2"/>     ← sets size state
 *   <text em="true"/>                ← sets bold state
 *   <text>Hello world</text>         ← prints text with current state
 */
const buildEposPrintMessage = (data: string | PrintRow[]): string => {
  let message = '';

  if (typeof data === 'string') {
    message += '<text>' + escapeMarkup(data) + '</text>';
  } else {
    for (const row of data) {
      const align = mapAlignment(row.alignment);
      const { width, height } = mapFontSize(row.fontSize);

      // Set alignment
      message += `<text align="${align}"/>`;

      // Set size if not default
      if (width > 1 || height > 1) {
        message += `<text width="${width}" height="${height}"/>`;
      }

      // Set double width/height
      if (row.doubleWidth || row.doubleHeigth) {
        message += `<text`;
        if (row.doubleWidth) message += ` dw="true"`;
        if (row.doubleHeigth) message += ` dh="true"`;
        message += `/>`;
      }

      // Set bold
      if (row.bold) {
        message += `<text em="true"/>`;
      }

      if (row.qr) {
        // QR code — uses symbol element with qrcode_model_2
        message += `<symbol type="qrcode_model_2" width="4">${escapeMarkup(row.content)}</symbol>`;
      } else {
        // Print text content
        message += '<text>' + escapeMarkup(row.content || ' ') + '</text>';
      }

      // Reset formatting after each row to avoid state bleeding
      if (width > 1 || height > 1) {
        message += '<text width="1" height="1"/>';
      }
      if (row.doubleWidth || row.doubleHeigth) {
        message += '<text dw="false" dh="false"/>';
      }
      if (row.bold) {
        message += '<text em="false"/>';
      }

      // Add line feed after each row
      message += '<feed line="1"/>';
    }
  }

  // Cut the paper
  message += '<cut type="feed"/>';

  return message;
};

/**
 * Wraps ePOS print commands in the SOAP envelope.
 * This matches the format from the epson-printer-sdk EpsonPrinter.request() method.
 */
const wrapInSoap = (printMessage: string): string => {
  const eposPrint =
    '<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">' +
    printMessage +
    '</epos-print>';

  return SOAP_HEADER + eposPrint + SOAP_FOOTER;
};

/**
 * Checks if a printer is an Epson (EpsonSocket) printer.
 */
export const isEpsonPrinter = (printerModel: string): boolean => {
  return printerModel === 'EpsonSocket' || printerModel.startsWith('TM-');
};

/**
 * Gets the ePOS endpoint URL for an Epson printer.
 * Includes devid and timeout query parameters as required by the ePOS protocol.
 */
const getEpsonUrl = (printerIp: string): string => {
  return `http://${printerIp}/cgi-bin/epos/service.cgi?devid=local_printer&timeout=10000`;
};

const EPOS_HEADERS = {
  'Content-Type': 'text/xml; charset=utf-8',
  'If-Modified-Since': 'Thu, 01 Jun 1970 00:00:00 GMT',
  SOAPAction: '""',
};

type PrinterStatus = {
  isResponsive: boolean;
  isOffline: boolean;
  coverIsOpen: boolean;
  paperNearEmpty: boolean;
  paperEmpty: boolean;
};

type FetchFn = (url: string, options: { method: string; headers: Record<string, string>; body: string }) => Promise<string>;

/**
 * Creates a fetch function that uses the bridge API when in webview mode,
 * falling back to regular fetch otherwise.
 */
const createBridgeFetch = (
  bridgeFetch: ReturnType<typeof useBridgeApi>['fetch'],
): FetchFn => {
  if (window.hasNativeWrapper) {
    return async (url, options) => {
      const result = await bridgeFetch(url, options);
      if ('error' in result) {
        throw new Error(result.error);
      }
      return result.body;
    };
  }

  return async (url, options) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), EPSON_REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      return await response.text();
    } catch (err) {
      clearTimeout(timeoutId);
      throw err;
    }
  };
};

/**
 * Sends an empty epos-print to check printer status.
 * This is the same approach as epson-printer-sdk's getStatus() — send an empty print
 * and parse the status bitmask from the response.
 *
 * Returns null if the printer is unreachable or the response is invalid.
 */
const getEpsonStatus = async (
  printerIp: string,
  doFetch: FetchFn,
): Promise<PrinterStatus | null> => {
  const emptyPrint =
    '<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print"></epos-print>';
  const soapPayload = SOAP_HEADER + emptyPrint + SOAP_FOOTER;

  try {
    const responseText = await doFetch(getEpsonUrl(printerIp), {
      method: 'POST',
      headers: EPOS_HEADERS,
      body: soapPayload,
    });

    const statusMatch = responseText.match(/status\s*=\s*"\s*(\d+)\s*"/);
    if (!statusMatch) {
      return null;
    }

    const status = parseInt(statusMatch[1], 10);

    return {
      isResponsive: !(status & 0x00000001),
      isOffline: !!(status & 0x00000008),
      coverIsOpen: !!(status & 0x00000020),
      paperNearEmpty: !!(status & 0x00020000),
      paperEmpty: !!(status & 0x00080000),
    };
  } catch {
    return null;
  }
};

/**
 * Sends an ePOS print request directly to the Epson printer via HTTP.
 * Uses the same request format as epson-printer-sdk's EpsonPrinter.request().
 */
const sendEposPrint = async (
  printerIp: string,
  soapPayload: string,
  doFetch: FetchFn,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const responseText = await doFetch(getEpsonUrl(printerIp), {
      method: 'POST',
      headers: EPOS_HEADERS,
      body: soapPayload,
    });

    // Parse the response — matches epson-printer-sdk response handling
    const successMatch = responseText.match(/success\s*=\s*"\s*(1|true)\s*"/);
    if (successMatch) {
      return { success: true };
    }

    // Extract error code
    const codeMatch = responseText.match(/code\s*=\s*"\s*(\S*)\s*"/);
    return {
      success: false,
      error: codeMatch ? codeMatch[1] : 'Unknown ePOS error',
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { success: false, error: `Network error: ${message}` };
  }
};

type Logger = {
  info: (msg: string, data?: any) => void;
  warn: (msg: string, data?: any) => void;
  error: (msg: string, data?: any) => void;
};

const STATUS_CHECK_MAX_RETRIES = 3;
const STATUS_CHECK_RETRY_DELAY_MS = 5000;

/**
 * Hook that returns a function to print via Epson direct HTTP.
 * Uses the bridge API fetch (with SSL validation disabled) when running inside the native webview,
 * and falls back to regular fetch when running in a browser.
 */
export const useEpsonDirectPrint = () => {
  const bridgeApi = useBridgeApi();
  const doFetch = createBridgeFetch(bridgeApi.fetch);

  /**
   * Sends a print to an Epson printer directly via HTTP.
   *
   * Flow:
   * 1. Status check (empty print) to confirm printer is ready and prevent duplicates
   * 2. If status check fails, retry it up to 3 times with 5s delays
   * 3. If status check never succeeds, try sending the print anyway as a last resort
   * 4. If the actual print fails, retry up to 3 times with 5s delays
   */
  const printViaEpsonWithRetry = useCallback(
    async (
      printerIp: string,
      data: string | PrintRow[],
      logger: Logger,
      base64Image?: string,
    ): Promise<boolean> => {
      let printMessage: string;

      // If we have a base64 image, convert it to ePOS raster format
      if (base64Image) {
        try {
          logger.info('Epson direct print: converting image to raster', { printerIp });
          const { rasterBase64, width, height } = await convertImageToEposRaster(base64Image);
          printMessage = `<image width="${width}" height="${height}" color="color_1" mode="mono">${rasterBase64}</image>`;
          // Add paper cut after image
          printMessage += '<cut type="feed"/>';
          logger.info('Epson direct print: image converted', {
            printerIp,
            imageWidth: width,
            imageHeight: height,
            rasterSize: rasterBase64.length,
          });
        } catch (err) {
          logger.error('Epson direct print: image conversion failed', {
            printerIp,
            errorMsg: err instanceof Error ? err.message : String(err),
          });
          return false;
        }
      } else {
        printMessage = buildEposPrintMessage(data);
      }

      const soapPayload = wrapInSoap(printMessage);

      logger.info('Epson direct print: built SOAP payload', {
        printerIp,
        payloadLength: soapPayload.length,
        hasImage: !!base64Image,
      });

      // Step 1: Status check — send empty print to verify printer is ready
      let statusCheckPassed = false;
      for (let attempt = 0; attempt < STATUS_CHECK_MAX_RETRIES; attempt++) {
        const status = await getEpsonStatus(printerIp, doFetch);

        if (status) {
          logger.info('Epson direct print: status check response', {
            printerIp,
            attempt,
            status,
          });

          if (!status.isResponsive || status.isOffline) {
            logger.warn('Epson direct print: printer not ready', {
              printerIp,
              attempt,
              isResponsive: status.isResponsive,
              isOffline: status.isOffline,
            });
          } else if (status.paperEmpty) {
            logger.warn('Epson direct print: paper empty', { printerIp });
          } else if (status.coverIsOpen) {
            logger.warn('Epson direct print: cover is open', { printerIp });
          } else {
            // Printer is ready
            statusCheckPassed = true;
            break;
          }
        } else {
          logger.warn('Epson direct print: status check failed (no response)', {
            printerIp,
            attempt,
          });
        }

        if (attempt < STATUS_CHECK_MAX_RETRIES - 1) {
          logger.info('Epson direct print: retrying status check', {
            nextRetryIn: `${STATUS_CHECK_RETRY_DELAY_MS / 1000}s`,
            attempt,
          });
          await new Promise((resolve) => setTimeout(resolve, STATUS_CHECK_RETRY_DELAY_MS));
        }
      }

      if (!statusCheckPassed) {
        logger.warn('Epson direct print: status checks exhausted, attempting print anyway', {
          printerIp,
        });
      }

      // Step 2: Send the actual print with retries
      const result = await sendEposPrint(printerIp, soapPayload, doFetch);

      if (result.success) {
        logger.info('Epson direct print: successful', {
          printerIp,
          statusCheckPassed,
        });
        return true;
      }

      logger.warn('Epson direct print: print failed', {
        printerIp,
        error: result.error,
      });

      return false;
    },
    [doFetch],
  );

  return { printViaEpsonWithRetry };
};
